import { ListSubheader } from '@mui/material';
import { DownloadFilesModal, FileToDownload } from 'components/atoms/DownloadFilesModal';
import { useRowSelectionContextOptional } from 'components/atoms/RowSelectionContext';
import { typeToName } from 'components/ReviewPanel/form.util';
import CohortQueryList from 'components/SearchFilters/CohortQueryList';
import { ResultsMode } from 'interfaces/experimentResults';
import { Permission } from 'interfaces/permissionOption';
import { ReviewForm } from 'interfaces/reviewForm';
import { Study } from 'interfaces/study';
import { isEmpty, map, omit, size, some } from 'lodash';
import React from 'react';
import { BooleanParam, StringParam, useQueryParam } from 'use-query-params';
import { exportFeatures, exportFormResults, exportProcedures, exportQcResults } from 'utils/exports';
import { useFormatBracketsOptions } from 'utils/formatBrackets/formatBracketsOptions';
import { useStudyStainTypeIds } from 'utils/queryHooks/stainType/useStudyStainTypes';
import { useCasesParams } from 'utils/useCasesParams';
import { manualSelectionFilters, paginationAndSortingParams } from 'utils/useCasesParams/constants';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { useEncodedFilters } from 'utils/useEncodedFilters';
import { usePermissions } from 'utils/usePermissions';
import { CellTableArtifactFile, discoverCellTableArtifactFiles } from './cellTableArtifactDiscovery';
import ExportProceduresModal from './ExportProceduresModal';
import SecondaryAnalysisExportMenuItems from './SecondaryAnalysisExportMenuItems';
import StudyExportMenuItem from './StudyExportMenuItem';
import { StudyExportOperation } from 'services/export/useStudyExportController';

interface StudyExportMenuListContentsProps {
  study?: Study;
  skipPreloadingStudyStains?: boolean; // If true, skip querying stain types in the study (i.e. when used in list of studies)
  disabled: boolean;
  forms?: ReviewForm[];
  immediateExportDisabled?: boolean;
  startImmediateExport?: (operation: StudyExportOperation) => Promise<boolean>;
}

const toCellTableDownloadFiles = (artifacts: CellTableArtifactFile[]): FileToDownload[] =>
  map(artifacts, (artifact) => ({
    id: artifact.id,
    url: artifact.urlForGetRequest,
    title: artifact.title,
    caseId: artifact.caseId,
    slideId: artifact.slideId,
    orchestrationId: artifact.orchestrationId,
  }));

const StudyExportMenuListContents: React.FunctionComponent<StudyExportMenuListContentsProps> = ({
  study,
  skipPreloadingStudyStains,
  disabled,
  forms,
  immediateExportDisabled = false,
  startImmediateExport = async ({ run }) => {
    await run(new AbortController().signal, () => {});
    return true;
  },
}) => {
  const activeStudyId = study?.id;

  const [, setSelectPrepareJobToExportModalStudyId] = useQueryParam(
    'selectPrepareJobToExportModalStudyId',
    StringParam
  );
  const [, setIntermediateResultsDownloadModalStudyId] = useQueryParam(
    'intermediateResultsDownloadModalStudyId',
    StringParam
  );

  const { generateEncodedParams, areFiltersAppliedExceptStudy, emptyFiltersExceptStudy } = useEncodedFilters();

  const { casesParams } = useCasesParams();

  const isSlidesMode = casesParams?.slidesMode;

  const hasSpecificSelections = some(manualSelectionFilters, (filterName) => !isEmpty(casesParams?.[filterName]));

  // Select-all is stored as omit mode with no omitted rows, so it leaves every ID list empty.
  const isSelectAllActive = useRowSelectionContextOptional()?.selectionMode === 'omit';
  const hasIntermediateResultsSelection = hasSpecificSelections || isSelectAllActive;

  const orchestrationId = casesParams.filters?.orchestrationId;
  const resultsMode = casesParams.resultsMode;

  const disabledFeatures = (resultsMode == ResultsMode.Published && !study?.hasFeatures) || !study?.allowExport;

  const { labId } = useCurrentLabId();

  const { hasPermission } = usePermissions();

  const canViewInternalLabels = hasPermission(Permission.ViewInternalSlideLabels);

  const canViewReviewMenu = hasPermission(Permission.ViewReviewMenu);

  const canDownloadIntermediateResults = hasPermission(Permission.DownloadIntermediateResults);

  const canDownloadCellsTable = hasPermission(Permission.DownloadCellsTable);

  const { data: stainTypesInStudy, isLoading: isLoadingStainTypes } = useStudyStainTypeIds(activeStudyId, {
    enabled: !disabled && Boolean(activeStudyId) && !skipPreloadingStudyStains,
  });
  const addStainToFeatureName = size(stainTypesInStudy) > 1;

  const formatBracketsOptions = useFormatBracketsOptions({
    addStain: addStainToFeatureName,
    studyId: activeStudyId,
  });

  const casesParamsWithoutPaginationAndSorting = {
    ...omit(casesParams, paginationAndSortingParams),
    studyId: activeStudyId,
    filters: {
      ...casesParams.filters,
      studyId: activeStudyId,
    },
  };
  const [cellsTableFiles, setCellsTableFiles] = React.useState<FileToDownload[] | null>(null);

  const discoverCellsTableFiles = (signal: AbortSignal) =>
    discoverCellTableArtifactFiles(casesParamsWithoutPaginationAndSorting, signal).then(toCellTableDownloadFiles);

  const refreshSelectedCellsTableFiles = async (selectedFiles: FileToDownload[], signal: AbortSignal) => {
    const refreshedFiles = await discoverCellsTableFiles(signal);
    const refreshedFilesById = new Map(map(refreshedFiles, (file) => [file.id, file]));
    const refreshedSelection = map(selectedFiles, (file) => refreshedFilesById.get(file.id)).filter(
      Boolean
    ) as FileToDownload[];
    if (refreshedSelection.length !== selectedFiles.length) {
      throw new Error('One or more selected cell table files are no longer available.');
    }
    return refreshedSelection;
  };

  const cellsTableArchiveScope = hasSpecificSelections
    ? 'selection'
    : areFiltersAppliedExceptStudy
    ? 'filtered'
    : 'whole-study';
  const cellsTableArchiveName = `cell_tables_study_${activeStudyId}_${cellsTableArchiveScope}.zip`;
  const cellsTableExportDisabled =
    !study?.allowExport || !study?.hasResults || (!orchestrationId && resultsMode === ResultsMode.Manual);
  const cellsTableExportDisabledMessage = !study?.allowExport
    ? 'Exports are disabled for this study'
    : !study?.hasResults
    ? 'No results to export'
    : 'Select an orchestration to enable cells table export';

  const exportAllFeaturesOptions = {
    studyId: activeStudyId,
    context: {
      ...formatBracketsOptions,
      orchestrationId,
      skipPreloadingStudyStains: isLoadingStainTypes || skipPreloadingStudyStains,
      labId,
      studyId: activeStudyId,
    },
    resultsMode,
    ...(resultsMode === ResultsMode.Manual && { orchestrationId }),
  };

  const casesParamsWithoutSelections = omit(casesParamsWithoutPaginationAndSorting, manualSelectionFilters);
  const casesParamsWithoutStudyIdOrSelections = {
    ...omit(casesParamsWithoutSelections, ['studyId']),
    filters: omit(casesParamsWithoutSelections?.filters, 'studyId'),
  };

  const exportLabelResults = (signal: AbortSignal) =>
    exportQcResults({
      labId,
      studyId: activeStudyId,
      context: {
        ...formatBracketsOptions,
        orchestrationId,
        skipPreloadingStudyStains: isLoadingStainTypes || skipPreloadingStudyStains,
        labId,
        studyId: activeStudyId,
      },
      resultsMode,
      ...(resultsMode === ResultsMode.Manual && { orchestrationId }),
      signal,
    });

  const [isExportProceduresModalOpen, setIsExportProceduresModalOpen] = useQueryParam(
    'isExportProceduresModalOpen',
    BooleanParam
  );

  const handleExportProcedures = () => {
    if (!areFiltersAppliedExceptStudy) {
      void startImmediateExport({
        title: 'Dataset metadata export',
        canCancel: true,
        run: (signal) =>
          exportProcedures(
            activeStudyId,
            generateEncodedParams({ skipPagination: true, ...emptyFiltersExceptStudy }),
            signal
          ),
      });
    } else {
      setIsExportProceduresModalOpen(true);
    }
  };

  const resultsOrchestrationText =
    resultsMode === ResultsMode.InternallyApproved
      ? 'Internally approved'
      : resultsMode === ResultsMode.Manual
      ? orchestrationId
        ? `Orchestration ${orchestrationId}`
        : 'Latest'
      : '';

  return (
    <>
      <ListSubheader>Case / Slide Metadata / Form Replies</ListSubheader>
      <StudyExportMenuItem
        title="Dataset metadata (.csv)"
        disabled={!study?.allowExport || immediateExportDisabled}
        disabledMessage="Exports are disabled for this study"
        onClick={handleExportProcedures}
        secondaryText={areFiltersAppliedExceptStudy ? 'Current filtered dataset or whole dataset' : 'Whole dataset'}
      />
      {isExportProceduresModalOpen && (
        <ExportProceduresModal
          onClose={() => setIsExportProceduresModalOpen(false)}
          onExportFiltered={() =>
            startImmediateExport({
              title: 'Filtered dataset metadata export',
              canCancel: true,
              run: (signal) => exportProcedures(activeStudyId, generateEncodedParams({ skipPagination: true }), signal),
            })
          }
          onExportAll={() =>
            startImmediateExport({
              title: 'Dataset metadata export',
              canCancel: true,
              run: (signal) =>
                exportProcedures(
                  activeStudyId,
                  generateEncodedParams({ skipPagination: true, ...emptyFiltersExceptStudy }),
                  signal
                ),
            })
          }
        />
      )}
      {canViewInternalLabels && (
        <StudyExportMenuItem
          disabled={isEmpty(study?.qcLabelsConfig) || immediateExportDisabled}
          disabledMessage="No QC Labels Configured for this study"
          onClick={() =>
            void startImmediateExport({
              title: 'Label results export',
              canCancel: true,
              run: exportLabelResults,
            })
          }
          title="Label Results (.csv)"
        />
      )}
      {canViewReviewMenu &&
        // TODO: change when new form permissions are implemented
        map(forms, (form) => (
          <StudyExportMenuItem
            key={form.id}
            disabled={immediateExportDisabled}
            onClick={() =>
              void startImmediateExport({
                title: `${typeToName(form.type)} responses export`,
                canCancel: true,
                run: (signal) => exportFormResults(form.id, signal),
              })
            }
            title={`Export ${typeToName(form.type)} Responses`}
          />
        ))}

      <ListSubheader>Cell and Area Tables</ListSubheader>
      <StudyExportMenuItem
        disabled={!study?.allowExport || !study?.hasResults}
        disabledMessage={study?.allowExport ? 'No results to export' : 'Exports are disabled for this study'}
        onClick={() => setSelectPrepareJobToExportModalStudyId(activeStudyId)}
        title="Prepared Zip Bundles for Customer Results"
      />
      {canDownloadCellsTable && (
        <>
          <StudyExportMenuItem
            disabled={cellsTableExportDisabled || immediateExportDisabled}
            disabledMessage={cellsTableExportDisabledMessage}
            onClick={() =>
              void startImmediateExport({
                title: 'Cells table export',
                canCancel: true,
                closeOnSuccess: true,
                initialStage: 'Gathering cell table files...',
                run: async (signal, reportProgress) => {
                  const files = await discoverCellTableArtifactFiles(
                    casesParamsWithoutPaginationAndSorting,
                    signal,
                    undefined,
                    ({ completedPages, inspectedSlides, discoveredFiles }) =>
                      reportProgress({
                        stage: `${completedPages} pages, ${inspectedSlides} slides, ${discoveredFiles} files discovered`,
                      })
                  );
                  setCellsTableFiles(toCellTableDownloadFiles(files));
                },
              })
            }
            title="Cells Table Parquet Files"
            secondaryText={
              hasSpecificSelections
                ? 'Selected Slides/Cases'
                : areFiltersAppliedExceptStudy
                ? 'Filtered Slides/Cases'
                : 'Whole Study'
            }
            tooltip={
              orchestrationId
                ? `Export cells table for orchestration ${orchestrationId}`
                : resultsMode === ResultsMode.Manual
                ? 'Select an orchestration to enable cells table export'
                : `Export cells table for the currently ${resultsMode} results`
            }
          />
          <DownloadFilesModal
            open={Boolean(cellsTableFiles)}
            onClose={() => setCellsTableFiles(null)}
            filesToDownload={cellsTableFiles || []}
            dialogTitle="Download Cells Table Parquets"
            emptyMessage="No cell table files are available for the active selection."
            zipConfiguration={{
              archiveName: cellsTableArchiveName,
              refreshFiles: refreshSelectedCellsTableFiles,
            }}
          />
        </>
      )}
      {/* Get intermediate results */}
      {canDownloadIntermediateResults && (
        <StudyExportMenuItem
          onClick={() => setIntermediateResultsDownloadModalStudyId(activeStudyId)}
          title="Intermediate Results..."
          secondaryText={
            !hasIntermediateResultsSelection
              ? `Select specific ${isSlidesMode ? 'slide' : 'case'} first`
              : 'Select individual job results to download'
          }
          disabled={!hasIntermediateResultsSelection}
        />
      )}
      <ListSubheader>Features{resultsOrchestrationText ? ` (${resultsOrchestrationText})` : ''}</ListSubheader>
      <StudyExportMenuItem
        disabled={disabledFeatures || immediateExportDisabled}
        isLoading={formatBracketsOptions.isLoading}
        onClick={() =>
          void startImmediateExport({
            title: 'Features export',
            canCancel: true,
            run: (signal) => exportFeatures({ ...exportAllFeaturesOptions, hideExcluded: true, signal }),
          })
        }
        title={`Whole study without excluded cases`}
        secondaryText={areFiltersAppliedExceptStudy ? 'Unfiltered' : undefined}
        tooltip={`${
          resultsMode === ResultsMode.InternallyApproved
            ? 'You are on Internally Approved mode- export internally approved results without excluded cases'
            : resultsMode === ResultsMode.Manual
            ? orchestrationId
              ? `You are on Manual mode- export results for selected orchestration (${orchestrationId}) without excluded cases`
              : 'You are on Manual mode- export latest result per slide without excluded cases'
            : ''
        }`}
        disabledMessage={study?.allowExport ? 'No features to export' : 'Exports are disabled for this study'}
      />
      <StudyExportMenuItem
        disabled={disabledFeatures || immediateExportDisabled}
        isLoading={formatBracketsOptions.isLoading}
        onClick={() =>
          void startImmediateExport({
            title: 'Features export',
            canCancel: true,
            run: (signal) => exportFeatures({ ...exportAllFeaturesOptions, signal }),
          })
        }
        title="With excluded cases"
        secondaryText={areFiltersAppliedExceptStudy ? 'Unfiltered' : undefined}
        tooltip={`${
          resultsMode === ResultsMode.InternallyApproved
            ? 'You are on Internally Approved mode- export internally approved results'
            : resultsMode === ResultsMode.Manual
            ? orchestrationId
              ? `You are on Manual mode- export results for selected orchestration (${orchestrationId})`
              : 'You are on Manual mode- export latest result per slide'
            : ''
        }`}
        disabledMessage={study?.allowExport ? 'No features to export' : 'Exports are disabled for this study'}
      />
      {hasSpecificSelections && (
        <StudyExportMenuItem
          disabled={disabledFeatures || immediateExportDisabled}
          onClick={() =>
            void startImmediateExport({
              title: 'Selected features export',
              canCancel: true,
              run: (signal) =>
                exportFeatures({
                  ...exportAllFeaturesOptions,
                  casesParams: casesParamsWithoutPaginationAndSorting,
                  signal,
                }),
            })
          }
          title={`Only selected ${isSlidesMode ? 'slides' : 'cases'}`}
          tooltip={`${
            resultsMode === ResultsMode.InternallyApproved
              ? 'You are on Internally Approved mode- export internally approved results'
              : resultsMode === ResultsMode.Manual
              ? orchestrationId
                ? `You are on Manual mode- export results for selected orchestration (${orchestrationId})`
                : 'You are on Manual mode- export latest result per slide'
              : ''
          }`}
        />
      )}
      {areFiltersAppliedExceptStudy && (
        <StudyExportMenuItem
          disabled={disabledFeatures || immediateExportDisabled}
          onClick={() =>
            void startImmediateExport({
              title: 'Filtered features export',
              canCancel: true,
              run: (signal) =>
                exportFeatures({ ...exportAllFeaturesOptions, casesParams: casesParamsWithoutSelections, signal }),
            })
          }
          title={`${isSlidesMode ? 'Slides' : 'Cases'} matching current filters`}
          secondaryText={hasSpecificSelections ? 'Including unselected' : undefined}
          tooltip={
            <>
              {resultsMode === ResultsMode.InternallyApproved
                ? 'You are on Internally Approved mode- export internally approved results'
                : resultsMode === ResultsMode.Manual
                ? orchestrationId
                  ? `You are on Manual mode- export results for selected orchestration (${orchestrationId})`
                  : 'You are on Manual mode- export latest result per slide'
                : 'Export results'}{' '}
              matching the following criteria
              <CohortQueryList queryObject={casesParamsWithoutStudyIdOrSelections} showIcon />
            </>
          }
        />
      )}
      <SecondaryAnalysisExportMenuItems
        study={study}
        immediateExportDisabled={immediateExportDisabled}
        startImmediateExport={startImmediateExport}
      />
      {!isEmpty(study?.highlightedFeatures) && (
        <StudyExportMenuItem
          disabled={disabledFeatures || immediateExportDisabled}
          onClick={() =>
            void startImmediateExport({
              title: 'Highlighted features export',
              canCancel: true,
              run: (signal) => exportFeatures({ ...exportAllFeaturesOptions, highlightedOnly: true, signal }),
            })
          }
          disabledMessage={
            study?.allowExport ? 'No highlighted features to export' : 'Exports are disabled for this study'
          }
          title={`Highlighted ${resultsOrchestrationText}Features only`}
          tooltip={`${
            resultsMode === ResultsMode.InternallyApproved
              ? 'You are on Internally Approved mode- export internally approved highlighted results'
              : resultsMode === ResultsMode.Manual
              ? orchestrationId
                ? `You are on Manual mode- export highlighted results for selected orchestration (${orchestrationId})`
                : 'You are on Manual mode- export latest highlighted result per slide'
              : ''
          }`}
        />
      )}
    </>
  );
};

export default StudyExportMenuListContents;
